import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import type { AuditRow, EmployeeRef, FlagCategory } from '../api/types';

/* ---------------------------------------------------------------------------
   Cross-cutting patterns from the brief. Each of these exists once and is
   reused everywhere the situation arises, so the behaviour cannot drift
   between screens.
   --------------------------------------------------------------------------- */

/**
 * Row-level conflict. A second save on an already-changed record is blocked
 * and the user is told to reload — never a silent overwrite.
 */
export function ConflictBanner({ message, onReload }: { message: string; onReload: () => void }) {
  return (
    <div className="banner banner--conflict" role="alert">
      <div>
        <strong>This record was changed by someone else.</strong>{' '}
        {message || 'Reload to see the latest version before acting on it.'}
      </div>
      <button className="btn btn--sm" style={{ background: 'var(--orange)', color: '#fff', border: 'none' }} onClick={onReload}>
        Reload record
      </button>
    </div>
  );
}

export function ErrorBanner({ message, onDismiss }: { message: string; onDismiss?: () => void }) {
  return (
    <div className="banner banner--error" role="alert">
      <div>{message}</div>
      {onDismiss && <button className="btn btn--sm" onClick={onDismiss}>Dismiss</button>}
    </div>
  );
}

/**
 * A locked period is read-only forever. Nothing is overwritten by the next
 * period — it is only superseded — so the wording says "locked", not "closed".
 */
export function LockedBanner({ periodName }: { periodName: string }) {
  return (
    <div className="banner banner--locked">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ width: 14, height: 14, border: '2px solid var(--muted)', borderRadius: 3, flex: 'none' }} />
        <span>
          <strong>{periodName}</strong> is locked. Records dated inside it stay viewable but can no longer be edited.
        </span>
      </div>
    </div>
  );
}

export function GraceBanner({ periodName, days }: { periodName: string; days: number }) {
  return (
    <div className="banner banner--info">
      <div>
        <strong>{periodName} is in its grace window</strong> ({days} days). Only HR/Admin can still edit records dated
        inside it; everyone else can view but not change them.
      </div>
    </div>
  );
}

/**
 * An employee id with no row in the HR master. Show the id and say why the
 * name is missing — never a blank cell, and never a broken row.
 */
export function EmployeeName({ employee, subtext }: { employee: EmployeeRef; subtext?: string | null }) {
  if (!employee.resolved) {
    return (
      <div>
        <span className="unresolved">Employee {employee.employeeId}</span>
        <div className="unresolved__note">not in employee master</div>
      </div>
    );
  }
  return (
    <div>
      <span>{employee.name}</span>
      {subtext && <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--muted)' }}>{subtext}</div>}
    </div>
  );
}

/** Flags fall into three visual categories: neutral/good, needs-attention, urgent. */
export function FlagPill({ flag, category, tooltip }: { flag: string; category: FlagCategory; tooltip?: string }) {
  return (
    <span className={`pill pill--flag pill--${category}`} title={tooltip}>
      {flag}
    </span>
  );
}

export function StatusPill({ label, color, bg }: { label: string; color: string; bg: string }) {
  return <span className="pill" style={{ background: bg, color }}>{label}</span>;
}

export function Points({ value }: { value: number }) {
  const color = value < 0 ? 'var(--red)' : value > 0 ? 'var(--green)' : 'var(--muted)';
  const label = value === 0 ? '—' : value > 0 ? `+${trim(value)}` : trim(value);
  return <span style={{ fontWeight: 700, color }}>{label}</span>;
}

/**
 * Audit "view history": one expandable inline trail, identical whether the
 * record lives in the HR database or the facility database. Users never learn
 * there are two.
 */
export function HistoryPanel({ rows, loading }: { rows?: AuditRow[]; loading: boolean }) {
  return (
    <>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--muted)', letterSpacing: 0.3, marginBottom: 6 }}>
        HISTORY
      </div>
      {loading && <div className="meta">Loading…</div>}
      {!loading && (!rows || rows.length === 0) && (
        <div className="meta">No changes recorded yet — this record is exactly as it was generated.</div>
      )}
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
        {rows?.map((r) => (
          <div key={r.auditId}>
            {formatDateTime(r.changedAtUtc)} — {r.summary ?? `${r.action} by ${r.changedBy}`}
          </div>
        ))}
      </div>
    </>
  );
}

/**
 * Location filter. Offers only the facilities the person's access reaches on
 * this screen. "All locations" appears only when that really is all of them —
 * offering it to a two-plant manager would promise a breadth they do not
 * have — and the control locks when there is exactly one.
 */
export function LocationSelect({
  facilities, allowed, value, onChange,
}: {
  facilities: { code: string; name: string }[];
  /** Codes this screen's capability reaches. null or omitted = every location. */
  allowed?: string[] | null;
  value: string;
  onChange: (code: string) => void;
}) {
  const everywhere = allowed == null;
  const reachable = everywhere ? facilities : facilities.filter((f) => (allowed ?? []).includes(f.code));
  const only = allowed && allowed.length === 1 ? allowed[0] : null;
  return (
    <select
      className="control"
      value={only ?? value}
      disabled={!!only}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Location"
      title={only ? 'Your access is scoped to this location' : undefined}
    >
      {!only && <option value="all">{everywhere ? 'All locations' : 'All my locations'}</option>}
      {/* Employees whose export carried no location import without error and
          then vanish from every location view. Filterable so they can be found,
          by people whose access covers every location. */}
      {everywhere && <option value="__none__">— No location set —</option>}
      {reachable.map((f) => <option key={f.code} value={f.code}>{f.name}</option>)}
    </select>
  );
}

/** CSV / Excel / PDF over the current filtered and sorted view, not the whole table. */
export function ExportMenu({ onExport }: { onExport: (format: 'csv' | 'excel' | 'pdf') => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button className="btn" onClick={() => setOpen((v) => !v)}>Export ▾</button>
      {open && (
        <div className="export-menu">
          {(['csv', 'excel', 'pdf'] as const).map((f) => (
            <button key={f} onClick={() => { setOpen(false); onExport(f); }}>
              {f === 'csv' ? 'Download CSV' : f === 'excel' ? 'Download Excel' : 'Download PDF'}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A metric card that navigates carrying its filter must show that filter as a
 * removable chip on arrival — not a silent pre-filter, or the short list looks
 * like missing data.
 */
export function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <button className="chip" onClick={onClear}>
      {label} <span style={{ fontSize: 12 }}>✕</span>
    </button>
  );
}

export function SortableHeader({
  label, column, sort, onSort, className,
}: {
  label: string;
  column: string;
  sort: { column: string; dir: 'asc' | 'desc' };
  onSort: (column: string) => void;
  className?: string;
}) {
  const arrow = sort.column === column ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '';
  return (
    <th className={`sortable ${className ?? ''}`} onClick={() => onSort(column)}>
      {label}{arrow}
    </th>
  );
}

export function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="spinner-note">{children}</td>
    </tr>
  );
}

/* ---------------------------------------------------------------------------
   Formatting
   --------------------------------------------------------------------------- */

export function trim(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}

/** Dates come back as plain yyyy-MM-dd; parse them as local so the day never shifts. */
export function parseDate(iso: string): Date {
  const [y, m, d] = iso.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function formatDate(iso: string): string {
  return parseDate(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function formatLongDate(iso: string): string {
  return parseDate(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Downloads the rows the user is actually looking at. */
/** A person as the employee picker shows them. */
export interface PickablePerson {
  employeeId: number;
  displayName: string;
  department?: string | null;
  locationCode?: string | null;
}

/** How a person reads once picked: "Tonice Bridgefort (15)". */
export function personLabel(p: PickablePerson): string {
  return `${p.displayName} (${p.employeeId})`;
}

/**
 * Search-as-you-type employee picker. The app draws it rather than using the
 * browser's <datalist>, which cannot be styled, spaces its rows for touch, and
 * gives no sign that more matches exist. This lists every match in a box that
 * scrolls, sized so the last visible row is cut in half and plainly continues,
 * and always says how many there are. Scroll to browse; type to narrow.
 *
 * Resolves to one person on a picked suggestion, a bare ID, or a full name
 * that belongs to exactly one person. Anything else is a search in progress
 * and reports null. To clear it from outside, change its key.
 */
export function EmployeePicker({
  id, people, value, onChange, placeholder = 'Search by name or ID…',
}: {
  id: string;
  people: PickablePerson[];
  value: number | null;
  onChange: (employeeId: number | null) => void;
  placeholder?: string;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  // Set while the mouse is down inside the list. Grabbing its scrollbar takes
  // focus off the input, and that blur must not close the list.
  const pressingList = useRef(false);
  const listId = `${id}-list`;

  const matches = useMemo(() => rankPeople(people, query), [people, query]);

  // Keep the highlighted row in view as the arrow keys move past the box's edge.
  useEffect(() => {
    if (!open || !matches[active]) return;
    document.getElementById(`${listId}-${matches[active].employeeId}`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active, matches, listId]);

  // Releasing the mouse anywhere ends a press that began in the list, and
  // hands focus back to the input so typing carries on.
  useEffect(() => {
    const release = () => {
      if (!pressingList.current) return;
      pressingList.current = false;
      inputRef.current?.focus();
    };
    window.addEventListener('mouseup', release);
    return () => window.removeEventListener('mouseup', release);
  }, []);

  const choose = (p: PickablePerson) => {
    pressingList.current = false;
    setQuery(personLabel(p));
    setOpen(false);
    onChange(p.employeeId);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => (matches.length === 0 ? 0 : (i + step + matches.length) % matches.length));
    } else if (e.key === 'Enter' && open && matches[active]) {
      // Enter picks the highlighted person rather than submitting anything.
      e.preventDefault();
      choose(matches[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const unresolved = !open && query.trim() !== '' && value === null;

  return (
    <div className="picker">
      <input
        ref={inputRef}
        id={id}
        className="control"
        style={{ width: '100%' }}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && matches[active] ? `${listId}-${matches[active].employeeId}` : undefined}
        autoComplete="off"
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActive(0);
          onChange(resolvePerson(people, e.target.value)?.employeeId ?? null);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => { if (!pressingList.current) setOpen(false); }}
        onKeyDown={onKeyDown}
      />
      {open && (
        <div className="picker__list" onMouseDown={() => { pressingList.current = true; }}>
          {matches.length > 0 && (
            <ul className="picker__scroll" id={listId} role="listbox">
              {matches.map((p, i) => (
                <li
                  key={p.employeeId}
                  id={`${listId}-${p.employeeId}`}
                  role="option"
                  aria-selected={i === active}
                  className={`picker__option${i === active ? ' picker__option--active' : ''}`}
                  // mousedown, not click: picking has to beat the input's blur.
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); choose(p); }}
                  onMouseEnter={() => setActive(i)}
                >
                  <div className="picker__name">
                    <Highlight text={p.displayName} term={query} /> <span className="picker__id">({p.employeeId})</span>
                  </div>
                  {(p.locationCode || p.department) && (
                    <div className="picker__sub">{[p.locationCode, p.department].filter(Boolean).join(' · ')}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className="picker__footer">{footerText(matches.length, query)}</div>
        </div>
      )}
      {unresolved && <div className="meta" style={{ marginTop: 4 }}>No match yet. Choose someone from the list.</div>}
    </div>
  );
}

/** Rows the list shows before it scrolls; keep in step with .picker__scroll. */
const VISIBLE_ROWS = 7;

function footerText(count: number, query: string): string {
  if (count === 0) return `No one matches “${query.trim()}”.`;
  if (query.trim() === '') return `${count} people. Scroll or type to narrow.`;
  const found = `${count} ${count === 1 ? 'match' : 'matches'}`;
  return count > VISIBLE_ROWS ? `${found}. Scroll or keep typing to narrow.` : found;
}

/** A picked label searches by its name part, so reopening the list still finds the person. */
function searchTerm(text: string): string {
  return text.trim().toLowerCase().replace(/\s*\(\d+\)$/, '');
}

/**
 * Best matches first: an exact ID or a name that starts with the term, then
 * any word or ID that starts with it, then a name that merely contains it.
 * Ties keep the order the roster arrived in (by surname).
 */
function rankPeople(people: PickablePerson[], text: string): PickablePerson[] {
  const t = searchTerm(text);
  if (!t) return people;
  const rank = (p: PickablePerson) => {
    const name = p.displayName.toLowerCase();
    const id = String(p.employeeId);
    if (id === t || name.startsWith(t)) return 0;
    if (id.startsWith(t) || name.split(/\s+/).some((w) => w.startsWith(t))) return 1;
    if (name.includes(t)) return 2;
    return -1;
  };
  return people
    .map((p, index) => ({ p, index, r: rank(p) }))
    .filter((x) => x.r >= 0)
    .sort((a, b) => a.r - b.r || a.index - b.index)
    .map((x) => x.p);
}

function resolvePerson(people: PickablePerson[], text: string): PickablePerson | undefined {
  const t = text.trim().toLowerCase();
  if (!t) return undefined;
  const exact = people.find((p) => personLabel(p).toLowerCase() === t || String(p.employeeId) === t);
  if (exact) return exact;
  const byName = people.filter((p) => p.displayName.toLowerCase() === t);
  return byName.length === 1 ? byName[0] : undefined;
}

function Highlight({ text, term }: { text: string; term: string }) {
  const t = searchTerm(term);
  const i = t ? text.toLowerCase().indexOf(t) : -1;
  if (i < 0) return <>{text}</>;
  return <>{text.slice(0, i)}<mark>{text.slice(i, i + t.length)}</mark>{text.slice(i + t.length)}</>;
}

export function downloadCsv(filename: string, headers: string[], rows: (string | number | null | undefined)[][]) {
  const escape = (v: string | number | null | undefined) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers, ...rows].map((r) => r.map(escape).join(',')).join('\r\n');
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
